import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, TablePagination, InputBase, alpha,
  Avatar, Alert, Menu, MenuItem, Snackbar, Tooltip, useTheme, useMediaQuery,
  Tabs, Tab, Stack,
} from '@mui/material';
import {
  Plus, Search, MoreVertical, Trash2, Edit, ExternalLink, FileText, Send, BookOpen, Sparkles
} from 'lucide-react';
import axios from '../utils/axios';
import { getBlogMainImageUrl } from '../utils/helpers';
import BlogModal from '../components/BlogModal';
import AutoBlogModal from '../components/AutoBlogModal';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import LoadingState from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';

const SITE = 'https://www.resellerbazzar.in';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const BlogsView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAutoBlogOpen, setIsAutoBlogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionBlog, setActionBlog] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setPage(0); }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, limit: rowsPerPage, search: debouncedSearch };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get('/admin/blogs', { params });
      setBlogs(res.data.blogs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleToggleStatus = async (targetBlog, newStatus) => {
    try {
      const fd = new FormData();
      fd.append('status', newStatus);
      await axios.put(`/admin/blogs/${targetBlog._id}`, fd, { skipNotify: 'success' });
      setSnackbar({
        open: true,
        message: `Blog ${newStatus === 'published' ? 'published' : 'moved to draft'} successfully!`,
        severity: 'success',
      });
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.msg || 'Failed to update status', severity: 'error' });
    }
  };

  const openNew = () => { setEditingBlog(null); setIsModalOpen(true); };
  const handleMenuOpen = (e, blog) => { setMenuAnchorEl(e.currentTarget); setActionBlog(blog); };
  const handleMenuClose = () => { setMenuAnchorEl(null); setActionBlog(null); };
  const handleEdit = () => { setEditingBlog(actionBlog); setIsModalOpen(true); handleMenuClose(); };
  const handleDeleteClick = () => { setDeleteDialogOpen(true); setMenuAnchorEl(null); };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/admin/blogs/${actionBlog._id}`, { skipNotify: 'success' });
      setSnackbar({ open: true, message: 'Blog deleted successfully', severity: 'success' });
      // Step back a page if we just removed the last row on it.
      if (blogs.length === 1 && page > 0) setPage((p) => p - 1);
      else load();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.msg || 'Failed to delete', severity: 'error' });
    }
    setDeleteDialogOpen(false);
    setActionBlog(null);
  };

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Blogs"
        subtitle="Create, edit, and publish SEO-optimized articles for the merchant portal and marketplace."
        count={total}
        countLabel="blogs"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Content', href: '/blogs' },
          { label: 'Blogs' }
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={openNew}
            sx={{ fontWeight: 700 }}
          >
            New Blog
          </Button>
        }
        secondaryAction={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Sparkles size={17} />}
            onClick={() => setIsAutoBlogOpen(true)}
            sx={{
              fontWeight: 700,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              borderColor: (t) => alpha(t.palette.primary.main, 0.35),
              '&:hover': {
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                borderColor: 'primary.main',
              }
            }}
          >
            Auto Blog (AI)
          </Button>
        }
      />

      <Card sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={statusFilter}
            onChange={(e, val) => { setStatusFilter(val); setPage(0); }}
            sx={{
              minHeight: 38,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.875rem', minHeight: 38, py: 0.5, px: 2 }
            }}
          >
            <Tab label="All" value="all" />
            <Tab label="Published" value="published" />
            <Tab label="Drafts" value="draft" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, border: 1, borderColor: 'divider', borderRadius: 2, width: { xs: '100%', sm: 280 } }}>
              <Search size={18} color={theme.palette.text.secondary} />
              <InputBase
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.trimStart())}
                placeholder="Search by title, category..."
                sx={{ ml: 1, flex: 1, fontSize: '0.875rem' }}
              />
            </Box>
            <ExportTableButton
              data={blogs}
              total={total}
              onExportAll={async () => {
                const params = { all: true, search: debouncedSearch };
                if (statusFilter !== 'all') params.status = statusFilter;
                const res = await axios.get('/admin/blogs', { params });
                return res.data.blogs || [];
              }}
              columns={[
                { label: 'Title', key: 'title' },
                { label: 'Slug', key: 'slug' },
                { label: 'Category', value: (r) => r.category_name || r.category || '' },
                { label: 'Status', key: 'status' },
                { label: 'Views', key: 'views' },
                { label: 'Created At', value: (r) => fmtDate(r.createdAt || r.created_at) },
              ]}
              filename="blogs"
            />
          </Box>
        </Box>

        {error ? (
          <Box sx={{ px: 3, py: 2 }}><Alert severity="error">{error}</Alert></Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 550, sm: 700 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Blog</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <LoadingState rows={rowsPerPage} cols={5} />
                ) : blogs.length > 0 ? (
                  blogs.map((blog) => {
                    const isDraft = blog.status === 'draft';
                    return (
                      <TableRow key={blog._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                            <Avatar
                              variant="rounded"
                              src={getBlogMainImageUrl(blog.blog_main_image)}
                              sx={{ width: 52, height: 40, bgcolor: 'background.neutral', color: 'text.disabled' }}
                            >
                              <FileText size={18} />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{blog.title || 'Untitled'}</Typography>
                                {blog.auto_generated && (
                                  <Tooltip title={`Auto-generated by AI (${blog.generation_source || 'Gemini Pro'})`}>
                                    <Chip
                                      icon={<Sparkles size={11} />}
                                      label={blog.target_market && blog.target_market !== 'All' ? `AI · ${blog.target_market}` : 'AI'}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.6875rem',
                                        fontWeight: 800,
                                        bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
                                        color: 'secondary.dark',
                                        '& .MuiChip-icon': { color: 'inherit', ml: 0.5 }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                              <Typography sx={{ fontSize: 12, color: 'text.disabled' }} noWrap>/blog/{blog.url}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{blog.category_name || '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={isDraft ? 'Draft' : 'Published'}
                              size="small"
                              sx={{
                                fontWeight: 700, borderRadius: 1,
                                bgcolor: alpha(isDraft ? theme.palette.warning.main : theme.palette.success.main, 0.12),
                                color: isDraft ? theme.palette.warning.dark : theme.palette.success.dark,
                              }}
                            />
                            {isDraft ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<Send size={13} />}
                                onClick={() => handleToggleStatus(blog, 'published')}
                                sx={{ borderRadius: 1.5, fontSize: 11, fontWeight: 700, py: 0.2, px: 1.25, minWidth: 'auto', textTransform: 'none' }}
                              >
                                Publish
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<FileText size={13} />}
                                onClick={() => handleToggleStatus(blog, 'draft')}
                                sx={{ borderRadius: 1.5, fontSize: 11, fontWeight: 700, py: 0.2, px: 1.25, minWidth: 'auto', textTransform: 'none' }}
                              >
                                Unpublish
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{fmtDate(blog.created_at)}</TableCell>
                        <TableCell align="right">
                          {!isDraft && (
                            <Tooltip title="View on site">
                              <IconButton aria-label="View on site" size="small" component="a" href={`${SITE}/blog/${blog.url}`} target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary' }}>
                                <ExternalLink size={17} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, blog)}>
                            <MoreVertical size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                      <EmptyState
                        icon={BookOpen}
                        title="No blogs found"
                        description={searchTerm || statusFilter !== 'all' ? "No blog articles match your active search or status filter." : "Create your first article to share marketplace insights, tips, and updates."}
                        actionLabel={searchTerm || statusFilter !== 'all' ? "Clear Filters" : "New Blog"}
                        onAction={searchTerm || statusFilter !== 'all' ? () => { setSearchTerm(''); setStatusFilter('all'); setPage(0); } : openNew}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 1.5 }, borderTop: 1, borderColor: 'divider' }}>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { width: 170, borderRadius: 2 } }}
      >
        {actionBlog?.status === 'draft' ? (
          <MenuItem onClick={() => { handleToggleStatus(actionBlog, 'published'); handleMenuClose(); }} sx={{ color: 'success.main', fontSize: '0.875rem', gap: 1, fontWeight: 700 }}>
            <Send size={16} /> Publish Blog
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { handleToggleStatus(actionBlog, 'draft'); handleMenuClose(); }} sx={{ color: 'warning.main', fontSize: '0.875rem', gap: 1, fontWeight: 700 }}>
            <FileText size={16} /> Move to Draft
          </MenuItem>
        )}
        <MenuItem onClick={handleEdit} sx={{ fontSize: '0.875rem', gap: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      <BlogModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        blog={editingBlog}
        onSuccess={() => {
          load();
          setSnackbar({ open: true, message: `Blog ${editingBlog ? 'updated' : 'created'} successfully!`, severity: 'success' });
        }}
      />

      <AutoBlogModal
        open={isAutoBlogOpen}
        onClose={() => setIsAutoBlogOpen(false)}
        onBlogCreated={() => {
          load();
          setSnackbar({ open: true, message: 'AI Blog generated and saved successfully!', severity: 'success' });
        }}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Blog"
        content={`Are you sure you want to delete "${actionBlog?.title || 'this blog'}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogsView;
